import React, { useEffect, useRef } from "react";
import bodyHtml from "./salary-calculator-body.content";
import jsonLdHtml from "./salary-calculator-jsonld.content";
import calculatorLogic from "./salary-calculator-logic.content";
import "./salary-calculator.css";

const XLSX_SCRIPT =
  "https://cdn.jsdelivr.net/npm/xlsx-js-style@1.2.0/dist/xlsx.bundle.js";
const LUCIDE_SCRIPT =
  "https://unpkg.com/lucide@0.378.0/dist/umd/lucide.min.js";

const INIT_BLOCK = `document.addEventListener('DOMContentLoaded', function() {
  try {
    lucide.createIcons();
  } catch (e) {
    console.warn('Icon library failed to load; icons will be missing but the calculator still works.', e);
  }
  calculate();
  initTocScrollSpy();
});`;

const PATCHED_INIT_BLOCK = `function initSalaryCalculator() {
  try {
    lucide.createIcons();
  } catch (e) {
    console.warn('Icon library failed to load; icons will be missing but the calculator still works.', e);
  }
  calculate();
  initTocScrollSpy();
}
document.addEventListener('DOMContentLoaded', initSalaryCalculator);
if (document.readyState !== 'loading') {
  initSalaryCalculator();
}`;

const loadExternalScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load salary calculator script: ${src}`));
    document.body.appendChild(script);
  });

const injectJsonLd = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html;

  container.querySelectorAll("script").forEach((oldScript) => {
    const script = document.createElement("script");
    script.type = oldScript.type;
    script.textContent = oldScript.textContent;
    document.head.appendChild(script);
  });
};

const SalaryCalculator = () => {
  const rootRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (
      initializedRef.current ||
      !rootRef.current ||
      typeof window.__salaryCalculatorInitialized !== "undefined"
    ) {
      return undefined;
    }

    let cancelled = false;

    const initializeCalculator = async () => {
      await loadExternalScript(XLSX_SCRIPT);
      await loadExternalScript(LUCIDE_SCRIPT);

      if (cancelled || typeof window.__salaryCalculatorInitialized !== "undefined") {
        return;
      }

      injectJsonLd(jsonLdHtml);

      const logic = calculatorLogic.replace(INIT_BLOCK, PATCHED_INIT_BLOCK);
      const script = document.createElement("script");
      script.textContent = logic;
      document.body.appendChild(script);

      window.__salaryCalculatorInitialized = true;
      initializedRef.current = true;
    };

    initializeCalculator().catch((error) => {
      console.error("Salary calculator failed to initialize.", error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      id="greythr-salary-calculator"
      ref={rootRef}
      dangerouslySetInnerHTML={{ __html: bodyHtml }}
    />
  );
};

export default SalaryCalculator;
